/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable function-paren-newline */
/* eslint-disable consistent-return */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { STOCKBIT_ACADEMY_URL } from 'constants/api';
import { academyEventAnalytic, academyUserProfiling } from 'utils/clevertap';
import { effects as chatEffects } from 'lib/entities/chat/slice';
import { PROJECT_FOLDER } from 'constants/app';
import dynamic from 'next/dynamic';
import { copyTextToClipboard } from 'utils/general';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import SUCCESS_MESSAGE from 'constants/successMessage';
import useTokenStore from './store';
import { Container } from './styled';

const ModalShare = dynamic(() => import('global/ModalShare'), { ssr: false });

function Academy() {
  const dispatch = useDispatch();
  const router = useRouter();
  const [iframeUrl, setIframeUrl] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  const generateToken = useTokenStore((store) => store.createToken);
  const academyToken = useTokenStore((store) => store.token);

  /**
   * Handle Navigate Event from Academy
   * @description change url in window
   * @param eventName event name from academy.stockbit.com
   * @param parameter event paramenter
   */
  const handleNavigateEvent = (eventName, parameter = undefined) => {
    if (!parameter || !eventName) {
      return;
    }

    const isUnboxingList = eventName
      .toLowerCase()
      .includes('unboxing saham viewed');
    const isArticleViewed = eventName
      .toLowerCase()
      .includes('unboxing article viewed');
    const isVolumeChanged = eventName
      .toLowerCase()
      .includes('unboxing volume changed');

    // Should only apply to `/academy/unboxing` and `/unboxing` only.
    if (isUnboxingList) {
      // Academy webapp resides on both /academy/[[..slugs]] and /unboxing/[[..slugs]]
      const windowUrl = router.asPath.startsWith('/academy')
        ? '/academy/unboxing'
        : '/unboxing';

      router.replace(windowUrl, windowUrl, { shallow: true });
      return;
    }

    // Should only apply to `/academy/unboxing/{id}` and `/unboxing/volume/{id}` only.
    if (isVolumeChanged || isArticleViewed) {
      // Academy webapp resides on both /academy/[[..slugs]] and /unboxing/[[..slugs]]
      let windowUrl = router.asPath.startsWith('/academy')
        ? '/academy/unboxing'
        : '/unboxing';

      // unboxing volume
      const volume = Number(parameter.Volume);
      if (volume) {
        const subpath = router.asPath.startsWith('/academy')
          ? '/academy/unboxing'
          : '/unboxing/volume';
        windowUrl = `${subpath}/${volume}`;
      }

      router.replace(windowUrl, windowUrl, { shallow: true });
      return;
    }

    // Lines 68-94 are the default conditions.
    // It applies to anything else
    // (`/academy`, `/academy/modules/1`, `/academy/modules/1/chapters/3`, etc).

    const params: any = {
      modules: Number(parameter['Module ID']),
      chapters: Number(parameter['Chapter ID']),
      lessons: Number(parameter['Lesson ID']),
    };

    // remove undefined params
    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    // change browser window url on academy event
    if (Object.keys(params).length > 0) {
      let windowUrl = '/academy';
      Object.keys(params).forEach((param) => {
        windowUrl = `${windowUrl}/${param}/${params[param]}`;
      });

      router.replace(windowUrl, windowUrl, { shallow: true });
      return;
    }

    router.replace('/academy', '/academy', { shallow: true });
  };

  const academyEventListener = (event) => {
    if (!event.data || typeof event.data !== 'string') {
      return;
    }

    let postMessageData = { fn: undefined, data: undefined };

    try {
      postMessageData = JSON.parse(event.data);
    } catch (error) {
      return;
    }

    // check if postmessage contains fn and data.
    if (!postMessageData.fn && postMessageData.data) {
      return;
    }

    const { fn, data } = postMessageData;

    if (fn === 'academyAnalyticsEvent') {
      academyEventAnalytic(data.event_name, data.parameter);
    }

    if (fn === 'academyAnalyticsEvent' && data.action === 'navigation') {
      return handleNavigateEvent(data.event_name, data.parameter);
    }

    if (fn === 'unboxingVolume') {
      return handleNavigateEvent('Unboxing Volume Changed', data.parameter);
    }

    // User Profiling event
    if (fn === 'academyUserProfilingTracker') {
      return academyUserProfiling(data);
    }

    if (fn === 'academyShareUnboxingEvent') {
      setShowShareModal(true);
    }

    if (fn === 'academyNavigationEvent') {
      window.open(data.url, data.target);
    }

    if (fn === 'popstate') {
      // When `academy` webapp in iframe try to reauthenticate with `/auth`,
      // it means users want to get out of the page. They hit the browser's
      // back buttton until it reaches academy home page.
      if (data.href.includes('/auth')) {
        // Just pop the last 2 records that consist of iframe and the previous page url.
        // We don't need to store or track the last previous route/pathname and
        // "replace" the current URL with it. Simpler and not clever.
        window.history.go(-2);
      }
    }
  };

  const getRedirectToUrl = () => {
    const { asPath } = router;

    // remove unessesary url query
    const redirectUrl = new URLSearchParams(window.location.search);

    redirectUrl.delete('refresh');
    redirectUrl.delete('source');
    redirectUrl.delete('shared');

    const isDefaultPath =
      asPath === '/academy/' ||
      asPath === '/academy' ||
      asPath.includes('?refresh=1');

    if (isDefaultPath) {
      return '/';
    }

    let redirectToUrl = '';

    // Redirect special case lesson page with query params
    if (redirectUrl.has('lessonId')) {
      redirectToUrl = redirectUrl.toString();
      redirectToUrl = `?${redirectToUrl}`;
    }

    const urlpath = asPath.split('?')[0];
    return (
      urlpath
        .replace('/academy', '')
        .replace('/academy/', '')
        .replace('/volume', '') + redirectToUrl
    );
  };

  const generateIframeUrl = () => {
    const UNIX_TIMESTAMP = Math.floor(Date.now() / 1000);

    const query: any = {
      token: academyToken,
      timestamp: UNIX_TIMESTAMP,
    };

    if (router.query.redirect_to) {
      query.redirect_to = router.query.redirect_to;
    } else {
      query.redirect_to = getRedirectToUrl();
    }

    const urlParams = new URLSearchParams(query);

    setIframeUrl(`${STOCKBIT_ACADEMY_URL}/auth?${urlParams}`);
  };

  useEffect(() => {
    generateToken();
    // replace current style on main container to prevent overflow with height
    document.getElementById('main-container').setAttribute(
      'style',
      `
          margin-top: 0px !important;
          max-width: unset;
          width: auto;
          padding-right: 0px;
          margin-left: 0px;
          height: 100vh;
          position: relative;
        `,
    );
  }, []);

  useEffect(() => {
    generateIframeUrl();
    window.addEventListener('message', academyEventListener);

    return () => {
      window.removeEventListener('message', academyEventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyToken]);

  useEffect(() => {
    // Hard refresh if user click Academy Link on Navbar
    const isPathNameAllowed = router.asPath !== router.pathname;
    if (router.asPath.includes('refresh=1') && isPathNameAllowed) {
      generateIframeUrl();
    }
  }, [router.asPath]);

  const shareToChat = async (chatData) => {
    const send = await dispatch(
      chatEffects.sendBulkChat({
        ...chatData,
        attachment_url: '',
      }),
    );
    // @ts-ignore
    const { error } = send.payload;

    if (!error) {
      setShowShareModal(false);
    }
  };

  const sharedUrl = useMemo(() => {
    const path =
      !PROJECT_FOLDER || PROJECT_FOLDER === '/' ? '' : `/${PROJECT_FOLDER}`;
    if (router.asPath.includes('unboxing')) {
      const unboxingIdPath = router.asPath.substring(
        router.asPath.slice(1, -1).lastIndexOf('/') + 1,
      );
      return `${window.location.origin}${path}/unboxing/volume${unboxingIdPath}`;
    }
    return `${window.location.origin}${path}${router.asPath}?shared=true`;
  }, [router.asPath]);

  const handleCopyLink = () => {
    copyTextToClipboard(sharedUrl, () =>
      handleErrorMessageAPI(
        'Link Copied',
        SUCCESS_MESSAGE.SNACKBAR_GREEN,
        true,
      ),
    );
  };

  if (iframeUrl === '') {
    return null;
  }

  return (
    <>
      <Container
        allowFullScreen
        allow="autoplay"
        src={iframeUrl}
        id="academy-iframe"
      />
      <ModalShare
        visible={showShareModal}
        onCancel={() => setShowShareModal(false)}
        title="Share"
        sendChat={(chatData) => shareToChat(chatData)}
        hasSocialMedia
        hasShareInput
        hasCopyButton
        urlToShare={sharedUrl}
        defaultSharedMessage={sharedUrl}
        onCopy={handleCopyLink}
      />
    </>
  );
}

export default Academy;
